<template>
    <div class="ViewMap-MAIN">
        <div ref="mapEl" class="map"></div>
    </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

export default {
    name: "ViewMap",

    props: {
        searchTerm: { type: String, required: true },
        apiKey: { type: String, required: true },
        radius: { type: Number, default: 5000 },
        zoom: { type: Number, default: 14 }
    },

    data() {
        return {
            map: null,
            userMarker: null,
            userCircle: null,
            placeMarkers: [],
            infoWindow: null,
            lastCenter: null,

            dictionary: {
                "chilis": "chili's",
                "cinepolis": "cinépolis"
            }
        };
    },

    watch: {
        searchTerm(newVal) {
            if (this.lastCenter && this.map) {
                this.searchPlaces(this.lastCenter, newVal);
            }
        }
    },

    methods: {
        normalizedTerm(term) {
            const raw = (term || "").trim().toLowerCase();
            return this.dictionary[raw] || raw;
        },

        async loadGoogle() {
            const loader = new Loader({
                apiKey: this.apiKey,
                version: "quarterly"
            });

            return await loader.load();
        },

        async initMapAt(lat, lng) {
            await this.loadGoogle();

            const google = window.google;

            const center = new google.maps.LatLng(lat, lng);
            this.lastCenter = center;

            this.map = new google.maps.Map(this.$refs.mapEl, {
                center,
                zoom: this.zoom,

                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,

                gestureHandling: "none",
                zoomControl: false,
                keyboardShortcuts: false
            });

            this.infoWindow = new google.maps.InfoWindow();

            this.userMarker?.setMap(null);

            this.userMarker = new google.maps.Marker({
                position: center,
                map: this.map,
                title: "Mi ubicación",
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: "#4285F4",
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: "#fff"
                }
            });

            this.userCircle?.setMap(null);

            this.userCircle = new google.maps.Circle({
                map: this.map,
                center,
                radius: this.radius,
                fillColor: "#4285F4",
                fillOpacity: 0.12,
                strokeColor: "#4285F4",
                strokeOpacity: 0.6,
                strokeWeight: 2,
                clickable: false
            });

            this.fitMapToRadius(center);

            await this.searchPlaces(center, this.searchTerm);
        },

        clearPlaceMarkers() {
            this.placeMarkers.forEach(marker => marker.setMap(null));
            this.placeMarkers = [];
        },

        async searchPlaces(center, term) {
            if (!this.map) return;

            const google = window.google;

            const { Place } = await google.maps.importLibrary("places");

            const keyword = this.normalizedTerm(term);

            this.clearPlaceMarkers();

            try {
                const response = await Place.searchByText({
                    textQuery: keyword,
                    locationBias: {
                        center: {
                            lat: center.lat(),
                            lng: center.lng()
                        },
                        radius: this.radius
                    },
                    maxResultCount: 20,
                    fields: [
                        "displayName",
                        "location",
                        "formattedAddress",
                        "rating",
                        "googleMapsURI"
                    ]
                });

                const places = response?.places || [];

                const filtered = places.filter(place => {
                    if (!place.location) return false;

                    const dist = this.distanceMeters(
                        center.lat(),
                        center.lng(),
                        place.location.lat(),
                        place.location.lng()
                    );

                    if (dist > this.radius) {
                        return false;
                    }

                    const name =
                        typeof place.displayName === "string"
                            ? place.displayName
                            : place.displayName?.text || "";

                    return this.matchesBrand(name, keyword);
                });

                filtered.forEach(place => {
                    if (!place.location) return;

                    const marker = new google.maps.Marker({
                        map: this.map,
                        position: place.location,
                        title:
                            typeof place.displayName === "string"
                                ? place.displayName
                                : place.displayName?.text
                    });

                    marker.addListener("click", () => {
                        const name =
                            typeof place.displayName === "string"
                                ? place.displayName
                                : place.displayName?.text || "";

                        const distM = this.distanceMeters(
                            center.lat(),
                            center.lng(),
                            place.location.lat(),
                            place.location.lng()
                        );

                        this.infoWindow.setContent(`
                            <div style="min-width:220px">
                                <strong>${name}</strong><br/>
                                ${place.formattedAddress || ""}<br/>
                                ${place.rating ? `★ ${place.rating}` : ""}<br/>
                                ${Math.round(distM)} m
                            </div>
                        `);

                        this.infoWindow.open(this.map, marker);
                    });

                    this.placeMarkers.push(marker);
                });
            } catch (error) {
                console.error("Error buscando lugares:", error);
            }
        },

        fitMapToRadius(center) {
            const google = window.google;

            const bounds = new google.maps.LatLngBounds();

            const lat = center.lat();
            const lng = center.lng();

            const latOffset = this.radius / 111320;
            const lngOffset =
                this.radius /
                (111320 * Math.cos(lat * Math.PI / 180));

            bounds.extend({
                lat: lat + latOffset,
                lng: lng + lngOffset
            });

            bounds.extend({
                lat: lat - latOffset,
                lng: lng - lngOffset
            });

            this.map.fitBounds(bounds, 40);
        },

        normalizeForCompare(str) {
            return (str || "")
                .toLowerCase()
                .trim()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9\s]/g, " ")
                .replace(/\s+/g, " ")
                .trim();
        },

        matchesBrand(placeName, searchTerm) {
            const name = this.normalizeForCompare(placeName);
            const term = this.normalizeForCompare(searchTerm);

            if (!name || !term) return false;

            const stop = new Set([
                "la",
                "el",
                "los",
                "las",
                "de",
                "del",
                "y",
                "the",
                "a",
                "an",
                "hotel"
            ]);

            const tokens = term
                .split(" ")
                .filter(t => t && !stop.has(t));

            if (!tokens.length) return false;

            return name.includes(tokens.join(" "));
        },

        distanceMeters(lat1, lng1, lat2, lng2) {
            const R = 6371000;

            const toRad = x => (x * Math.PI) / 180;

            const dLat = toRad(lat2 - lat1);
            const dLng = toRad(lng2 - lng1);

            const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) *
                Math.cos(toRad(lat2)) *
                Math.sin(dLng / 2) ** 2;

            return 2 * R * Math.asin(Math.sqrt(a));
        },

        getLocation() {
            if (!("geolocation" in navigator)) {
                this.initMapAt(19.432608, -99.133209);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                pos => {
                    this.initMapAt(
                        pos.coords.latitude,
                        pos.coords.longitude
                    );
                },
                () => {
                    this.initMapAt(19.432608, -99.133209);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000
                }
            );
        }
    },

    mounted() {
        this.getLocation();
    }
};
</script>

<style scoped>
.ViewMap-MAIN {
    width: 100%;
}

.map {
    width: 100%;
    height: 450px;
    border: 0;
}
</style>